import React, {Fragment} from 'react';
import {flatten} from 'lodash';

import {emptyArray} from '@packages/utilities/fp/utils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {Atom, Plugins, withSystem} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {getCommerceDataProperty} from './ContextUtils';
import {
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import type {
  AtomRenderProps,
  ChildRender,
  ContextType,
} from '@packages/systems/wfdl';

import {TListOfElement, TRecord} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useTemplate} from '@packages/systems/render';
import {getChildren} from '@packages/utilities/data-utils';

const {SSR, EXPORT} = WebflowEnvironments;

const sampleExtraItems = [
  {
    id: null,
    name: '',
    price: {value: 0, unit: 'USD', string: '$0.00'},
  },
];

const getExtraItems = getCommerceDataProperty('extraItems', []);

const renderExtraItem =
  ({
    context,
    renderChild,
    children,
  }: {
    context: ContextType;
    renderChild: ChildRender;
    children: unknown[];
  }) =>
  (item: any, itemIndex: number) =>
    children.map((child) =>
      renderChild(
        child,
        // TODO (ecommerce): Memoize commerce context mutations
        context.set('data', item),
        // hack; when using mockOrder, we don't get ids
        `extraItem-${itemIndex}`,
        itemIndex
      )
    );

const createTemplateId = (id: string) => `wf-template-${id}`;

const renderExtraItems = (
  {renderChild, context, data}: AtomRenderProps,
  extraItems: Array<unknown>
) => {
  const renderExtraItemWithContext = renderExtraItem({
    context,
    renderChild,
    children: getChildren(data),
  });

  return extraItems.length > 0
    ? flatten(extraItems.map(renderExtraItemWithContext))
    : null;
};

const CommerceCheckoutExtraItemsList = Atom.createRender((prevProps) => {
  const {
    attributes,
    classes,
    context: prevContext,
    elementId,
    generateAttributes,
  } = prevProps;
  const extraItems = getExtraItems(prevContext);

  // TODO (ecommerce): Memoize commerce context mutations
  const context = prevContext
    .update('bindingPathPrefix', emptyArray, (bindingPathPrefix) =>
      bindingPathPrefix.concat('extraItems[]')
    )
    //.set('insideCommerceExtraItemsList', true);
    .set('insideCommerceOrderExtraItemsList', true);

  const props = {...prevProps, context} as const;
  const template = useTemplate();
  const runtimeEnvironment = useRuntimeEnvironment();

  if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
    const content = renderExtraItems(props, sampleExtraItems);
    const templateContent = template(content);
    const tagAttributes = {
      ...generateAttributes(attributes, classes),
      [WF_COLLECTION_DATA_KEY]: 'database.commerceOrder.extraItems',
      [WF_TEMPLATE_ID_DATA_KEY]: createTemplateId(elementId),
    } as const;
    return (
      <Fragment>
        <script type={WF_TEMPLATE_TYPE} id={createTemplateId(elementId)}>
          {templateContent}
        </script>
        <div {...tagAttributes}>{content}</div>
      </Fragment>
    );
  } else {
    return (
      <div {...generateAttributes(attributes, classes)}>
        {renderExtraItems(props, extraItems)}
      </div>
    );
  }
});

const config = createIntegratedAtom({
  displayName: 'Extra Items List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  showEmpty: true,
  constraints: {
    ancestors: [
      {is: 'CommerceCheckoutOrderSummaryWrapper', rule: 'ExactlyOne'},
    ],
    wraps: ['CommerceCheckoutOrderSummaryExtraItemsListItem'],
  },
  style: {
    base: {
      main: {},
    },
  },
  render: CommerceCheckoutExtraItemsList,
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    children: TListOfElement,
  },
});

export default config;
